// static/table_engine_src/core/spec_normalize.js

/**
 * Normalize spec -> derived table config:
 * - normalize columns (role/editor aliases)
 * - inject row-handle column (optional)
 * - seed hidden columns
 * - compute autoWidthMode + sizeMode
 * - provide helpers: colIndexByKey(), autoWidthKeys()
 */
export function normalizeSpec(spec) {
  if (!spec || typeof spec !== "object") return null;

  const rowMenuEnabled = spec.rowMenu === true;
  const rowMenuHoverEnabled = rowMenuEnabled && spec.rowMenuHover === true;

  const ROW_HANDLE_KEY = "__te_row__";
  const rowHandleCol = rowMenuEnabled
    ? {
        key: ROW_HANDLE_KEY,
        header: "#",
        kind: "rowHandle",
        width: Number.isFinite(spec.rowHandleWidth) ? spec.rowHandleWidth : 48,
        locked: true,
      }
    : null;

  const baseColsRaw = Array.isArray(spec.columns) ? spec.columns : [];

  // Normalize spec variants:
  // - accept {role:"input"/"output"/"actions"} as kind
  // - accept {editor:"number"} as type:"number"
  const baseCols = baseColsRaw.map((c) => {
    const col = { ...(c || {}) };

    if (!col.kind && col.role) {
      const r = String(col.role).toLowerCase();
      if (r === "input") col.kind = "input";
      else if (r === "output") col.kind = "output";
      else if (r === "actions") col.kind = "actions";
    }

    if (!col.type && col.editor) {
      const e = String(col.editor).toLowerCase();
      col.type = e === "number" ? "number" : e;
    }

    if (col.type != null) col.type = String(col.type).toLowerCase();

    // Accept `validate` as alias for `validation`.
    const validation =
      col.validation && typeof col.validation === "object"
        ? { ...col.validation }
        : (col.validate && typeof col.validate === "object" ? { ...col.validate } : null);
    if (validation) col.validation = validation;
    if (col.required === true) {
      col.validation = {
        ...(col.validation || {}),
        required: true,
      };
    }

    // Normalize semantic types into stable editor primitives.
    // type        -> canonical type (text|number|date|select)
    // semanticType-> original intent for validation/UX behavior
    const type0 = String(col.type || "text").toLowerCase();
    col.semanticType = String(col.semanticType || type0 || "text").toLowerCase();

    if (type0 === "enum") {
      col.type = "select";
      col.semanticType = "enum";

      const enumValues = Array.isArray(col.enumValues)
        ? col.enumValues
        : (Array.isArray(col.enum) ? col.enum : []);

      if (!Array.isArray(col.options) || !col.options.length) {
        col.options = enumValues.map((v) =>
          (v && typeof v === "object")
            ? v
            : ({ value: v == null ? "" : String(v), label: v == null ? "" : String(v) }));
      }

      if ((!Array.isArray(col.enumValues) || !col.enumValues.length) && Array.isArray(col.options)) {
        col.enumValues = col.options.map((o) =>
          (o && typeof o === "object" && o.value != null) ? o.value : o);
      }
    } else if (type0 === "percent") {
      col.type = "number";
      col.semanticType = "percent";
      if (!Number.isFinite(Number(col.scale))) col.scale = 100;
      col.format = { dp: 2, ...(col.format && typeof col.format === "object" ? col.format : {}) };
      if (!col.align) col.align = "right";
      if (!col.headerAlign) col.headerAlign = col.align;
    } else if (type0 === "currency") {
      col.type = "number";
      col.semanticType = "currency";
      col.format = {
        dp: 2,
        group: true,
        strip: false,
        ...(col.format && typeof col.format === "object" ? col.format : {}),
      };
      if (!col.align) col.align = "right";
      if (!col.headerAlign) col.headerAlign = col.align;
    } else if (type0 === "isin") {
      col.type = "text";
      col.semanticType = "isin";
      if (col.autoUppercase === undefined) col.autoUppercase = true;
      col.validation = {
        ...(col.validation && typeof col.validation === "object" ? col.validation : {}),
        minLength:
          Number.isFinite(Number(col.validation?.minLength)) ? Number(col.validation.minLength) : 12,
        maxLength:
          Number.isFinite(Number(col.validation?.maxLength)) ? Number(col.validation.maxLength) : 12,
      };
      if (!col.placeholder) col.placeholder = "ISIN";
    } else if (type0 === "id") {
      col.type = "text";
      col.semanticType = "id";
    } else {
      col.type = type0;
      col.semanticType = type0 || "text";
    }

    if (col.semanticType === "enum") {
      const enumValues = Array.isArray(col.enumValues) ? col.enumValues : [];
      if (enumValues.length) {
        col.validation = {
          ...(col.validation && typeof col.validation === "object" ? col.validation : {}),
          enum: Array.isArray(col.validation?.enum) && col.validation.enum.length
            ? col.validation.enum
            : enumValues.slice(),
        };
      }
    }

    // Date columns are usually fixed-width and should not trigger autosize churn.
    if (col.type === "date" && col.kind !== "output") {
      if (!col.placeholder) col.placeholder = "YYYY-MM-DD";
      if (col.nativeDateInput === undefined) col.nativeDateInput = false;
      if (col.autoWidth === undefined) col.autoWidth = false;
      if (col.width === undefined || col.width === null || col.width === "") col.width = 112;
      if (col.minWidth === undefined || col.minWidth === null || col.minWidth === "") col.minWidth = 104;
      if (col.maxWidth === undefined || col.maxWidth === null || col.maxWidth === "") col.maxWidth = 140;
      if (!col.align) col.align = "center";
      if (!col.headerAlign) col.headerAlign = col.align;
    }

    if (!col.kind) col.kind = "input";
    return col;
  });

  const hasUserRowHandle = baseCols.some(
    (c) => String((c && c.kind) || "").toLowerCase() === "rowhandle",
  );

  const showRowHandle = spec.showRowHandle !== false && spec.rowHandle !== false;
  const wantRowHandle = rowMenuEnabled && showRowHandle && !hasUserRowHandle;

  const cols = wantRowHandle ? [rowHandleCol, ...baseCols] : baseCols;
  const colByKey = new Map(cols.map((c) => [c.key, c]));

  function colIndexByKey(colKey) {
    if (!colKey) return -1;
    for (let i = 0; i < cols.length; i++) {
      if (cols[i] && cols[i].key === colKey) return i;
    }
    return -1;
  }

  // visible columns (hide/show)
  const hiddenCols = new Set();

  if (Array.isArray(spec.hiddenColumns)) {
    spec.hiddenColumns.forEach((k) => {
      if (k != null && String(k).trim() !== "") hiddenCols.add(String(k));
    });
  }

  cols.forEach((c) => {
    if (!c || !c.key) return;
    if (c.hidden === true) hiddenCols.add(c.key);
    if (c.visible === false) hiddenCols.add(c.key);
  });

  // Never hide row handle
  if (rowMenuEnabled) hiddenCols.delete(ROW_HANDLE_KEY);

  const spreadsheetEnabled = spec.spreadsheet !== false;
  const freezeLeftCols =
    Number.isFinite(spec.freezeLeftCols) && Number(spec.freezeLeftCols) > 0
      ? Math.trunc(Number(spec.freezeLeftCols))
      : 0;

  // auto width mode
  const autoWidthSetting = spec.autoWidth !== undefined ? spec.autoWidth : true;

  const autoWidthMode =
    autoWidthSetting === false ? "off" : autoWidthSetting === "all" ? "all" : "unsetOnly";

  // sizing mode (CSS hook)
  const sizeMode =
    typeof spec.sizeMode === "string" && spec.sizeMode
      ? String(spec.sizeMode).toLowerCase()
      : autoWidthMode === "off"
        ? "fill"
        : "auto";

  function autoWidthKeys() {
    return cols
      .filter((c) => {
        if (!c?.key) return false;
        if (hiddenCols.has(c.key)) return false;
        if (c.kind === "actions" || c.kind === "rowHandle") return false;
        if (c.autoWidth === false) return false;
        if (autoWidthMode === "all") return true;
        return c.width === undefined || c.width === null || c.width === "";
      })
      .map((c) => c.key);
  }

  return {
    cols,
    colByKey,
    hiddenCols,

    rowMenuEnabled,
    rowMenuHoverEnabled,
    ROW_HANDLE_KEY,

    spreadsheetEnabled,
    freezeLeftCols,

    autoWidthMode,
    sizeMode,
    autoWidthKeys,
    colIndexByKey,
  };
}
